import expertResponseFormatZh from '@/assets/docs/expert-response-format.md?raw';
import expertResponseFormatEn from '@/assets/docs/expert-response-format.en.md?raw';
import creatorResponseFormatZh from '@/assets/docs/creator-response-format.md?raw';
import creatorResponseFormatEn from '@/assets/docs/creator-response-format.en.md?raw';
import i18n from '@/lib/i18n';
import { toast } from 'sonner';

/**
 * 根据当前语言获取专家评审格式要求
 * @returns 对应语言的专家评审 Markdown 字符串
 */
export const getExpertResponseFormat = (): string => {
  const isEn = i18n?.language?.startsWith('en');
  return isEn ? expertResponseFormatEn : expertResponseFormatZh;
};

/**
 * 根据当前语言获取创作建议格式要求
 * @returns 对应语言的创作建议 Markdown 字符串
 */
export const getCreatorResponseFormat = (): string => {
  const isEn = i18n?.language?.startsWith('en');
  return isEn ? creatorResponseFormatEn : creatorResponseFormatZh;
};
export const NORMAL_EXIT_CODES = new Set([0, 143]);

export const normalizePositiveInt = (value: unknown, fallback: number): number => {
  const num = Number(value);
  if (!Number.isFinite(num) || num <= 0) {
    return fallback;
  }
  return Math.floor(num);
};

export const trimTailWithNotice = (text: string, maxChars: number, notice: string): string => {
  const str = String(text ?? '');
  const max = normalizePositiveInt(maxChars, 0);
  if (!max || str.length <= max) {
    return str;
  }
  const marker = typeof notice === 'string' ? notice : '';
  if (!marker || marker.length >= max) {
    return str.slice(str.length - max);
  }
  const tail = str.slice(str.length - (max - marker.length));
  return `${marker}${tail}`;
};

export const appendWithMaxAndNotice = (
  existingText: string,
  additionText: string,
  maxChars: number,
  notice: string,
) => {
  const existing = String(existingText ?? '');
  const addition = String(additionText ?? '');
  const max = normalizePositiveInt(maxChars, 0);
  const marker = typeof notice === 'string' ? notice : '';

  if (!max) {
    return { content: existing + addition, truncated: false };
  }

  if (existing.length >= max) {
    if (marker && marker.length < max) {
      return { content: existing.slice(0, max - marker.length) + marker, truncated: true };
    }
    return { content: existing.slice(0, max), truncated: true };
  }

  if (!addition) {
    return { content: existing, truncated: false };
  }

  const available = max - existing.length;
  if (addition.length <= available) {
    return { content: existing + addition, truncated: false };
  }

  if (!marker || marker.length >= max) {
    return { content: (existing + addition).slice(0, max), truncated: true };
  }

  if (available <= marker.length) {
    const base = existing.slice(0, Math.max(0, max - marker.length));
    return { content: base + marker, truncated: true };
  }

  const take = Math.max(0, available - marker.length);
  return { content: existing + addition.slice(0, take) + marker, truncated: true };
};

export const stringifyClaudeOutput = (value: unknown): string => {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

export const normalizeExitCode = (value: unknown): number => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

export const isNormalExitCode = (exitCode: number): boolean => NORMAL_EXIT_CODES.has(exitCode);

/**
 * 根据工具调用信息生成显示文本
 */
export function getToolCallMessageContent(toolName: string, toolInput: unknown): string {
  const input = toolInput as {
    file_path?: string;
    description?: string;
    path?: string;
    command?: string;
  };

  const t = (key: string, options?: Record<string, unknown>) =>
    i18n.t(`studio:build.status.${key}`, options);

  switch (toolName) {
    case 'Read':
      return t('reading_file', { file: input?.file_path || t('file_placeholder') });
    case 'Write':
      return t('writing_file', { file: input?.file_path || t('file_placeholder') });
    case 'Edit':
      return t('editing_file', { file: input?.file_path || t('file_placeholder') });
    case 'Bash':
      return t('executing_task', {
        task: input?.command || input?.description || t('task_placeholder'),
      });
    case 'Glob':
      return t('searching_directory', {
        path: input?.path || t('directory_placeholder'),
      });
    default:
      return toolName ? t('executing_tool', { name: toolName }) : (t('building_generic') as string);
  }
}

interface ApiMessageContent {
  type: string;
  text?: string;
  name?: string;
  input?: unknown;
  content?: string | unknown[];
  is_error?: boolean;
}

interface ApiMessage {
  id?: string;
  uuid?: string;
  type?: string;
  senderType?: string;
  timestamp?: number | string;
  createTime?: string;
  content?: string;
  message?: {
    role?: string;
    content?: string | ApiMessageContent[];
  };
}

/**
 * 移除字符串中的中文字符
 */
export function removeChinese(text: string): string {
  if (!text) return '';
  return text.replace(/[\u4e00-\u9fa5]/g, '');
}

/**
 * 检查消息内容是否包含角色定义（Role Description）
 */
export function isRoleDescription(content: string): boolean {
  if (!content) return false;
  // 匹配 ## Role Description, ### Role Description 等，且不区分大小写
  return /^#+\s*Role\s+Description/im.test(content);
}

/**
 * 将后端返回的原始消息格式化为前端统一的 Message 对象结构
 */
export function formatApiMessage(m: ApiMessage): Record<string, unknown> {
  const isAi = m.type === 'assistant' || m.senderType === 'AI';
  const timestamp = m.timestamp
    ? new Date(m.timestamp)
    : m.createTime
      ? new Date(m.createTime)
      : new Date();

  const t = (key: string, defaultValue?: string) =>
    i18n.t(`studio:build.status.${key}`, { defaultValue }) as string;

  if (isAi) {
    let aiContent = t('building_generic', 'Agent is performing tasks...');
    const rawContent = m.message?.content;

    if (typeof rawContent === 'string') {
      aiContent = rawContent;
    } else if (Array.isArray(rawContent)) {
      // 1. 尝试寻找文本回复
      const textBlock = rawContent.find((c) => c.type === 'text');
      // 2. 尝试寻找工具结果 (tool_result)
      const toolResult = rawContent.find((c) => c.type === 'tool_result');
      // 3. 尝试寻找带有效名称的工具调用 (tool_use)
      const toolUse = rawContent.find(
        (c) => c.type === 'tool_use' && c.name && c.name !== 'tool_use',
      );

      if (textBlock?.text) {
        aiContent = textBlock.text;
      } else if (toolResult) {
        try {
          const resultData =
            typeof toolResult.content === 'string'
              ? JSON.parse(toolResult.content)
              : toolResult.content;

          if (Array.isArray(resultData)) {
            aiContent =
              resultData[0]?.text || (toolResult.is_error ? t('task_error') : t('task_completed'));
          } else {
            aiContent = toolResult.is_error ? t('task_failed') : t('task_completed');
          }
        } catch {
          aiContent = toolResult.is_error ? t('task_failed') : t('task_completed');
        }
      } else if (toolUse) {
        aiContent = getToolCallMessageContent(toolUse.name as string, toolUse.input);
      } else {
        const anyToolUse = rawContent.find((c) => c.type === 'tool_use');
        if (anyToolUse) {
          aiContent = getToolCallMessageContent(
            (anyToolUse.name as string) || 'Task',
            anyToolUse.input,
          );
        }
      }
    }

    return {
      id: (m.id || m.uuid || `ai-${timestamp.getTime()}-${Math.random()}`).toString(),
      content: aiContent,
      role: 'assistant',
      timestamp: timestamp,
      status: 'done' as const,
    };
  } else {
    // 用户消息处理
    const msgContent =
      m.message && m.message.content
        ? typeof m.message.content === 'string'
          ? m.message.content
          : JSON.stringify(m.message.content)
        : m.content || '';

    return {
      id: (m.id || m.uuid || Date.now()).toString(),
      content: msgContent,
      role: 'user',
      timestamp: timestamp,
      status: 'done' as const,
    };
  }
}

/**
 * 解析包含在 ```json 代码块中的 JSON 内容
 * 返回清理后的文本和第一个解析出的对象
 */
export function parseMaybeJsonContent(text: string): {
  cleanText: string;
  parsed: Record<string, unknown> | null;
} {
  let cleanText = text.trim();
  let parsed: Record<string, unknown> | null = null;

  const jsonRegex = /```(?:json|JSON)\s*([\s\S]*?)```/g;
  const match = jsonRegex.exec(text);

  if (match) {
    const fullMatch = match[0];
    const jsonContent = match[1].trim();

    try {
      const data = JSON.parse(jsonContent);
      console.log('🚀 ~ [parseMaybeJsonContent] ~ data: ', data);
      if (data && typeof data === 'object' && 'type' in data) {
        parsed = data as Record<string, unknown>;
        cleanText = cleanText.replace(fullMatch, '');
      }
    } catch {
      toast.error(i18n.t('studio:build.status.parse_error'));
    }
  }

  return {
    cleanText: cleanText.trim(),
    parsed,
  };
}

/**
 * 解析文本中是否包含 API 错误或状态码
 * @param text 需要解析的字符串
 * @returns 包含是否为错误、状态码以及原始消息的对象
 */
export function parseErrorString(text: string): {
  isError: boolean;
  code?: number;
  message?: string;
} {
  if (!text) return { isError: false };

  // 匹配类似 "API Error: 504 error code: 504" 或其他包含状态码的常见错误格式
  const codeMatch =
    text.match(/error code:\s*(\d+)/i) ||
    text.match(/status code:\s*(\d+)/i) ||
    text.match(/API Error:\s*(\d+)/i);

  if (codeMatch && codeMatch[1]) {
    return { isError: true, code: parseInt(codeMatch[1], 10), message: text };
  }

  // 匹配其他明显的错误信息
  const lowerText = text.toLowerCase();
  if (lowerText.includes('api error:') || lowerText.includes('error:')) {
    return { isError: true, message: text };
  }

  return { isError: false };
}
